import React, { useEffect, useMemo, useRef, useState } from "react";

/**
 * TableWidget
 * - Recherche plein texte
 * - Tri sur l'en-tête (avec support nombres/dates)
 * - Pagination simple
 * - Ligne de sommes optionnelle
 * - Mise en évidence des échéances (deadline)
 * - Mise en évidence via filtres "Colorer"
 */
export default function TableWidget({
  title = "Tableau",
  rows = [],
  columns,
  rightSlot = null,
  pageSize = 7,
  footerSums = {},
  deadlineKey = "",
  blinkDanger = false,
  showSearch = true,
  showSort = true,
  thresholds = [],
  fullScreen = false,
  onExitFullScreen,
}) {
  // Colonnes visibles
  const cols = useMemo(() => {
    if (Array.isArray(columns) && columns.length) return columns;
    return [
      { key: "name", label: "Nom de fichier" },
      { key: "client", label: "Client" },
      { key: "date", label: "Date" },
      { key: "type", label: "Type" },
      { key: "__actions", label: "Actions", isActions: true },
    ];
  }, [columns]);

  // --- Recherche ------------------------------------------------------------
  const [search, setSearch] = useState("");
  const searchableCols = useMemo(
    () => cols.filter((c) => !c.isActions && c.key !== "__actions"),
    [cols]
  );
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      searchableCols.some((c) =>
        String(r?.[c.key] ?? "").toLowerCase().includes(q)
      )
    );
  }, [rows, search, searchableCols]);

  // --- Tri ------------------------------------------------------------------
  const [sort, setSort] = useState({ key: null, dir: "asc" });
  const toNum = (v) => {
    if (typeof v === "number") return v;
    if (v == null) return NaN;
    let s = String(v)
      .trim()
      .replace(/\u00A0/g, " ")
      .replace(/ /g, "");
    const lastComma = s.lastIndexOf(",");
    const lastDot = s.lastIndexOf(".");
    if (lastComma > lastDot) {
      s = s.replace(/\./g, "").replace(/,/g, ".");
    } else {
      s = s.replace(/,/g, "");
    }
    const n = Number(s);
    return Number.isNaN(n) ? NaN : n;
  };

  const onSort = (key, isActions) => {
    if (!showSort || isActions) return;
    setSort((s) => {
      if (s.key !== key) return { key, dir: "asc" };
      return { key, dir: s.dir === "asc" ? "desc" : "asc" };
    });
  };

  const sorted = useMemo(() => {
    if (!sort.key) return filtered;
    const { key, dir } = sort;
    if (deadlineKey && key === deadlineKey) {
      const arr = filtered.slice().sort((a, b) => {
        const da = Number(a?.__sort_deadline || 0);
        const db = Number(b?.__sort_deadline || 0);
        return da === db ? 0 : da - db;
      });
      return dir === "asc" ? arr : arr.slice().reverse();
    }

    const arr = filtered.slice().sort((a, b) => {
      const av = a?.[key];
      const bv = b?.[key];

      const na = toNum(av);
      const nb = toNum(bv);
      if (!Number.isNaN(na) && !Number.isNaN(nb)) {
        return na === nb ? 0 : na - nb;
      }

      const da = Date.parse(av);
      const db = Date.parse(bv);
      if (!Number.isNaN(da) && !Number.isNaN(db)) {
        return da === db ? 0 : da - db;
      }

      const sa = String(av ?? "").toLowerCase();
      const sb = String(bv ?? "").toLowerCase();
      return sa.localeCompare(sb);
    });
    return dir === "asc" ? arr : arr.reverse();
  }, [filtered, sort, deadlineKey]);

  // --- Pagination -----------------------------------------------------------
  const [page, setPage] = useState(1);
  useEffect(() => {
    setPage(1);
  }, [sorted.length, pageSize, search, sort.key, sort.dir]);

  const finalPageSize = Math.max(1, Number(pageSize) || 7);
  const pageCount = Math.max(1, Math.ceil(sorted.length / finalPageSize));
  const pagedRows = useMemo(() => {
    const start = (page - 1) * finalPageSize;
    return sorted.slice(start, start + finalPageSize);
  }, [sorted, page, finalPageSize]);

  // --- Apparition douce -----------------------------------------------------
  const tbodyRef = useRef(null);
  useEffect(() => {
    const tbody = tbodyRef.current;
    if (!tbody) return;
    const trs = Array.from(tbody.querySelectorAll("tr"));
    trs.forEach((tr, idx) => {
      tr.style.opacity = "0";
      tr.style.transform = "translateY(10px)";
      tr.style.transition = `opacity 0.3s ease ${idx * 0.05}s, transform 0.3s ease ${idx * 0.05}s`;
      requestAnimationFrame(() => {
        tr.style.opacity = "1";
        tr.style.transform = "translateY(0)";
      });
    });
  }, [page, pagedRows.length]);

  const getPaginationButtons = () => {
    const buttons = [];
    const maxVisible = 5;
    if (pageCount <= maxVisible) {
      for (let i = 1; i <= pageCount; i++) buttons.push(i);
    } else {
      buttons.push(1);
      let start = Math.max(2, page - 1);
      let end = Math.min(pageCount - 1, page + 1);
      if (page <= 3) end = 4;
      if (page >= pageCount - 2) start = pageCount - 3;
      if (start > 2) buttons.push("...");
      for (let i = start; i <= end; i++) buttons.push(i);
      if (end < pageCount - 1) buttons.push("...");
      buttons.push(pageCount);
    }
    return buttons;
  };

  const footerContent = (
    <>
      <div className="table-info">
        {sorted.length > 0
          ? `Affichage de ${(page - 1) * finalPageSize + 1} à ${Math.min(
              page * finalPageSize,
              sorted.length
            )} sur ${sorted.length}`
          : "Aucun élément"}
      </div>
      <div className="pagination">
        <button
          className="pagination-btn"
          disabled={page <= 1}
          onClick={() => setPage((p) => Math.max(1, p - 1))}
        >
          <i className="fas fa-chevron-left" />
        </button>
        {getPaginationButtons().map((pageNum, idx) =>
          pageNum === "..." ? (
            <span key={`ellipsis-${idx}`} className="pagination-ellipsis">...</span>
          ) : (
            <button
              key={pageNum}
              className={`pagination-btn ${pageNum === page ? "active" : ""}`}
              onClick={() => setPage(pageNum)}
            >
              {pageNum}
            </button>
          )
        )}
        <button
          className="pagination-btn"
          disabled={page >= pageCount}
          onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
        >
          <i className="fas fa-chevron-right" />
        </button>
      </div>
    </>
  );

  const getHighlightForCell = (row, key) => row?.__cellHighlights?.[key] || null;
  const isFixedColumn = (index) => index === 0;

  return (
    <div
      className={`table-container ${fullScreen ? "full-screen" : ""}`.trim()}
    >
      <div className="table-header">
        <h3 className="table-title">{title}</h3>
        <div className="table-filters">
          {rightSlot}
          {showSearch && (
            <div className="search-box">
              <i className="fas fa-search search-icon" />
              <input
                className="search-input"
                type="text"
                placeholder="Rechercher..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          )}
          {fullScreen && (
            <button
              className="btn btn-outline"
              onClick={typeof onExitFullScreen === "function" ? onExitFullScreen : () => window.history.back()}
            >
              <i className="fas fa-arrow-left" />
              Retour
            </button>
          )}
        </div>
      </div>

      {fullScreen && (
        <div className="table-footer table-footer-top">{footerContent}</div>
      )}

      <div className="table-scroll">
        <table className="table">
          <thead>
            <tr>
              {cols.map((c, index) => {
                const isSorted = sort.key === c.key;
                const icon =
                  !showSort || c.isActions
                    ? null
                    : isSorted
                    ? sort.dir === "asc"
                      ? "fa-sort-up"
                      : "fa-sort-down"
                    : "fa-sort";
                return (
                  <th
                    key={c.key}
                    onClick={() => onSort(c.key, c.isActions)}
                    className={`${(!showSort || c.isActions) ? "" : "sortable"} ${isFixedColumn(index) ? "fixed-column" : ""}`.trim()}
                    style={
                      !showSort || c.isActions ? {} : { cursor: "pointer" }
                    }
                  >
                    {c.label}
                    {icon && (
                      <i
                        className={`fas ${icon} sort-icon`}
                        style={{ marginLeft: 6 }}
                      />
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody ref={tbodyRef}>
            {pagedRows.length === 0 && (
              <tr>
                <td
                  colSpan={cols.length}
                  style={{
                    color: "#6b7280",
                    textAlign: "center",
                    padding: 16,
                  }}
                >
                  Aucun élément.
                </td>
              </tr>
            )}

            {pagedRows.map((r, i) => (
                <tr key={r?.__id ?? r?.id ?? i} className="table-row">
                  {cols.map((c, index) => {
                    if (c.isActions || c.key === "__actions") {
                      const a = r?.__actions || r?.actions || {};
                      return (
                        <td key={c.key}>
                          <div className="actions">
                            {a.onEdit && (
                              <button
                                className="action-btn"
                                title="Modifier"
                                onClick={a.onEdit}
                              >
                                <i className="fas fa-edit" />
                              </button>
                            )}
                          </div>
                        </td>
                      );
                    }

                    const isDeadlineCol = deadlineKey && c.key === deadlineKey;
                    const deadlineState = r?.__deadline || "none";
                    const dlClass = isDeadlineCol
                      ? `deadline ${deadlineState} ${
                          blinkDanger && deadlineState === "danger"
                            ? "blink"
                            : ""
                        }`
                      : "";
                    let thresholdClass = "";
                    if (
                      Array.isArray(thresholds) &&
                      thresholds.length &&
                      !c.isActions &&
                      c.key !== "__actions"
                    ) {
                      const rule = thresholds.find((t) => t?.field === c.key);
                      if (rule) {
                        const raw = r?.[c.key];
                        const num = toNum(raw);
                        if (
                          !Number.isNaN(num) &&
                          typeof rule.min === "number" &&
                          num < rule.min
                        ) {
                          thresholdClass = `threshold-low ${
                            blinkDanger ? "blink" : ""
                          }`;
                        }
                      }
                    }
                    const highlight = getHighlightForCell(r, c.key);
                    const rawValue = r?.[c.key];
                    const normalizedValue =
                      rawValue == null
                        ? ""
                        : typeof rawValue === "string" || typeof rawValue === "number"
                        ? rawValue
                        : String(rawValue);
                    let content = normalizedValue;
                    if (highlight) {
                      content = (
                        <span
                          className="cell-highlight-pill"
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            padding: "2px 8px",
                            borderRadius: "999px",
                            backgroundColor: highlight.backgroundColor,
                            color: highlight.textColor || "inherit",
                            lineHeight: 1.25,
                            fontWeight: 500,
                            maxWidth: "100%",
                          }}
                        >
                          <span
                            style={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              maxWidth: "100%",
                            }}
                          >
                            {normalizedValue}
                          </span>
                        </span>
                      );
                    }
                    return (
                      <td
                        key={c.key}
                        className={`${dlClass} ${thresholdClass} ${isFixedColumn(index) ? "fixed-column" : ""} ${highlight ? "cell-highlight" : ""}`.trim()}
                      >
                        {content}
                      </td>
                    );
                  })}
                </tr>
              ))}
          </tbody>

          {footerSums && Object.keys(footerSums).length > 0 && (
            <tfoot>
              <tr>
                {cols.map((c, idx) => {
                  if (c.isActions || c.key === "__actions")
                    return <td key={c.key} />;
                  if (idx === 0)
                    return (
                      <td key={c.key} className="sum-cell">
                        Total
                      </td>
                    );
                  const val = footerSums[c.key];
                  return (
                    <td key={c.key} className="sum-cell">
                      {typeof val === "number" ? val.toLocaleString() : ""}
                    </td>
                  );
                })}
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {!fullScreen && <div className="table-footer">{footerContent}</div>}
    </div>
  );
}
